import { useEffect, useState } from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { CheckBox, CheckBoxOutlineBlank, Clear, FilterAlt } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Weekdays } from "../../../Pickers/WeekdayPicker/weekday-picker.component";
import { weekdaysToToggle } from "../../functions/weekdayToToggle.function";
import { StyledToggleButton } from "./partial/StyledToggleButton.partial";
import { StyledToggleButtonGroup } from "./partial/StyledToggleButtonGroup.partial";

export function WeekdaysSelector({
  weekdays,
  setWeekdaysToggle
}: Readonly<{
  weekdays: Weekdays;
  setWeekdaysToggle: (w: Weekdays) => void;
  onEnableFetch: () => void;
}>) {
  const { t } = useTranslation();

  const [newToggles, setNewToggles] = useState<string[]>([]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { saturday, sunday, ...onlyWeekdays } = weekdays;

  // shared function to update toggle values and enable to trigger fetch
  const updateToggles = (toggles: string[]) => {
    setNewToggles(toggles);
    setWeekdaysToggle(weekdaysToToggle(toggles));
  };

  function handleWeekdayToggle(event: any, newToggles: string[]) {
    updateToggles(newToggles);
  }

  function clearWeekdayToggle() {
    updateToggles([]);
  }

  function selectAllWeekdayToggle() {
    updateToggles(["monday", "tuesday", "wednesday", "thursday", "friday"]);
  }

  useEffect(() => {
    // initially set weekdays toggle to selected weekdays only
    const initialVal = Object.entries(onlyWeekdays)
      .filter(([, selected]) => selected)
      .map(([key]) => key);
    setNewToggles(initialVal);
  }, []);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column" }}
      data-testid="capacity-weekdays-selector-box"
    >
      {/* weekdays toggle button group */}
      <StyledToggleButtonGroup
        value={newToggles}
        onChange={handleWeekdayToggle}
        orientation={"vertical"}
      >
        {Object.entries(onlyWeekdays).map(([k]) => (
          <StyledToggleButton
            key={k}
            disableRipple
            value={k}
            selected={newToggles.includes(k)}
            data-testid={`capacity-report-weekday-${k}-toggle-btn`}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {newToggles.includes(k) ? <CheckBox /> : <CheckBoxOutlineBlank />}
              {t(k)}
            </Box>
          </StyledToggleButton>
        ))}
      </StyledToggleButtonGroup>

      {/* select all toggle button */}
      <Tooltip
        title={<Typography>{t("Select all weekdays")}</Typography>}
        aria-label={"Select all weekdays"}
      >
        <Button
          onClick={selectAllWeekdayToggle}
          data-testid="select-all-weekdays-toggle-btn"
          sx={{ mt: 2 }}
        >
          <FilterAlt />
          {t("Select All")}
        </Button>
      </Tooltip>

      {/* clear toggle button */}
      {newToggles.length > 0 && (
        <Tooltip
          title={<Typography>{t("Clear weekday selection")}</Typography>}
          aria-label={"Clear weekday selection"}
        >
          <Button
            color={"secondary"}
            onClick={clearWeekdayToggle}
            data-testid="clear-weekdays-toggle-btn"
            sx={{ mt: 2 }}
          >
            <Clear />
            {t("Clear")}
          </Button>
        </Tooltip>
      )}
    </Box>
  );
}
